import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useState } from 'react';
import { lithology, type LithologyType, type LithologyTypeInput } from '@/api/endpoints';
import { Icon } from '@/components/Icon';
import { HatchingSwatch, PATTERNS, patternStyle } from '@/components/HatchingFill';
import { Pagination, usePaginated } from '@/components/Pagination';
import { SearchableSelect } from '@/components/SearchableSelect';

/**
 * Admin: katalog typów warstw geologicznych. Definiuje kod (Pż), nazwę, kolor + szrafurę.
 * Użytkownicy w edytorze profilu wybierają z tego katalogu.
 */
export function LithologyAdmin() {
  const [rows, setRows] = useState<LithologyType[] | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<LithologyType | null>(null);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reload = () => {
    setErr(null);
    setRows(null);
    lithology.list(includeInactive).then(setRows).catch(e => setErr(e?.message ?? String(e)));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [includeInactive]);

  const { pagedItems, page, pageSize, setPage, setPageSize, total } = usePaginated(rows ?? [], { storageKey: 'lithology' });

  const onDeactivate = async (l: LithologyType) => {
    if (!confirm(`Dezaktywować typ „${l.code} — ${l.name}"? Można ponownie aktywować edytując pole "active".`)) return;
    try {
      await lithology.delete(l.id);
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="max-w-[1400px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Typy warstw geologicznych</h1>
          <div className="text-ink-400 text-[13px]">
            Katalog wzorów (kolor + szrafura) używanych w profilach geologicznych otworów.
            Użytkownicy wybierają z tej listy w edytorze profilu.
          </div>
        </div>
        <div className="flex gap-2">
          <label className="text-[12px] text-ink-700 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)}/>
            Pokaż dezaktywowane
          </label>
          <button onClick={() => setCreating(true)} className="btn btn-primary btn-sm">
            <Icon name="plus" className="w-3.5 h-3.5"/> Nowy typ
          </button>
        </div>
      </header>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}

      <div className="card">
        <div className="card-body !p-0">
          {!rows && <div className="p-4 text-ink-400">Ładowanie…</div>}
          {rows && rows.length === 0 && <div className="p-4 text-ink-400">Brak typów. Dodaj pierwszy.</div>}
          {rows && rows.length > 0 && (
            <>
            <table className="table-hw">
              <thead>
                <tr>
                  <th className="w-16">Próbka</th>
                  <th>Kod</th>
                  <th>Nazwa</th>
                  <th>Opis</th>
                  <th>Wzór szrafury</th>
                  <th className="w-20">Kolor</th>
                  <th className="w-20">Sort</th>
                  <th className="w-24">Status</th>
                  <th className="w-20"></th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map(l => (
                  <tr key={l.id} className={l.active ? '' : 'opacity-50'}>
                    <td>
                      <span className="inline-block rounded border border-ink-200" style={{
                        width: 60, height: 32, ...patternStyle(l.pattern, l.color),
                      }} title={l.code + ' — ' + l.name}/>
                    </td>
                    <td className="font-mono text-ink-800">{l.code}</td>
                    <td className="text-ink-900">{l.name}</td>
                    {/* opis bywa zdaniem — zawijamy do 2 linii zamiast nowrap, który rozpychał tabelę poza kartę */}
                    <td className="text-[12px] text-ink-500">
                      <div className="max-w-[280px] line-clamp-2" title={l.description ?? ''}>{l.description ?? '—'}</div>
                    </td>
                    <td className="font-mono text-[11px] text-ink-700">{l.pattern}</td>
                    <td className="font-mono text-[11px] text-ink-700">{l.color}</td>
                    <td className="font-mono text-[11px] text-ink-500">{l.sortOrder}</td>
                    <td>{l.active ? <span className="pill pill-green">aktywny</span> : <span className="pill pill-amber">dezakt.</span>}</td>
                    <td className="text-right whitespace-nowrap">
                      <button onClick={() => setEditing(l)} className="text-water-600 hover:text-water-800 p-1" title="Edytuj">
                        <Icon name="edit" className="w-3.5 h-3.5"/>
                      </button>
                      {l.active && (
                        <button onClick={() => onDeactivate(l)} className="text-red-500 hover:text-red-700 p-1 ml-1" title="Dezaktywuj">
                          <Icon name="x" className="w-3.5 h-3.5"/>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-3 border-t border-ink-100">
              <Pagination total={total} page={page} pageSize={pageSize}
                          onPageChange={setPage} onPageSizeChange={setPageSize}/>
            </div>
            </>
          )}
        </div>
      </div>

      {(creating || editing) && (
        <LithologyEditModal
          initial={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); reload(); }}
        />
      )}
    </div>
  );
}

function LithologyEditModal({ initial, onClose, onSaved }: {
  initial: LithologyType | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = initial != null;
  const [form, setForm] = useState<LithologyTypeInput>({
    code: initial?.code ?? '',
    name: initial?.name ?? '',
    description: initial?.description ?? '',
    color: initial?.color ?? '#94a3b8',
    pattern: initial?.pattern ?? 'SOLID',
    sortOrder: initial?.sortOrder ?? 100,
    active: initial?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!form.code.trim()) { setErr('Kod jest wymagany'); return; }
    setBusy(true); setErr(null);
    try {
      if (isEdit && initial) await lithology.update(initial.id, form);
      else await lithology.create(form);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: form, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div className="bg-white rounded-lg shadow-2xl max-w-[600px] w-full" onClick={e => e.stopPropagation()}>
        <div className="border-b border-ink-100 px-5 py-3 flex items-center gap-3">
          <h2 className="text-[16px] font-semibold m-0">{isEdit ? 'Edycja typu warstwy' : 'Nowy typ warstwy'}</h2>
          <button onClick={requestClose} className="ml-auto text-ink-400 hover:text-ink-700"><Icon name="x"/></button>
        </div>

        <div className="p-5 space-y-3">
          {err && <div className="text-red-500 text-[12px]">{err}</div>}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Kod (np. Pż)*">
              <input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })}
                     className="input font-mono"/>
            </Field>
            <Field label="Nazwa*">
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                     className="input"/>
            </Field>
          </div>
          <Field label="Opis (opcjonalny)">
            <input value={form.description ?? ''} onChange={e => setForm({ ...form, description: e.target.value })}
                   className="input"/>
          </Field>

          <div className="grid grid-cols-3 gap-3 items-end">
            <Field label="Kolor">
              <div className="flex gap-1.5 items-center">
                <input type="color" value={form.color}
                       onChange={e => setForm({ ...form, color: e.target.value })}
                       className="w-10 h-9 rounded cursor-pointer border border-ink-200"/>
                <input value={form.color}
                       onChange={e => setForm({ ...form, color: e.target.value })}
                       className="input font-mono text-[11px]"/>
              </div>
            </Field>
            <Field label="Szrafura">
              <SearchableSelect value={form.pattern}
                      onChange={v => setForm({ ...form, pattern: v })}>
                {PATTERNS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </SearchableSelect>
            </Field>
            <Field label="Kolejność">
              <input type="number" value={form.sortOrder ?? 100}
                     onChange={e => setForm({ ...form, sortOrder: Number(e.target.value) })}
                     className="input font-mono"/>
            </Field>
          </div>

          <div>
            <Field label="Podgląd próbki">
              <div className="flex items-center gap-3 p-3 border border-ink-100 rounded">
                <HatchingSwatch pattern={form.pattern} color={form.color} className="!w-16 !h-12"/>
                <div>
                  <div className="font-mono text-[13px] font-semibold">{form.code || '(kod)'}</div>
                  <div className="text-[12px] text-ink-700">{form.name || '(nazwa)'}</div>
                </div>
              </div>
            </Field>
          </div>

          {isEdit && (
            <label className="flex items-center gap-2 text-[12px] text-ink-700 cursor-pointer">
              <input type="checkbox" checked={form.active ?? true}
                     onChange={e => setForm({ ...form, active: e.target.checked })}/>
              Aktywny (widoczny w dropdownie edytora profilu)
            </label>
          )}
        </div>

        <div className="border-t border-ink-100 px-5 py-3 flex justify-end gap-2">
          <button onClick={requestClose} disabled={busy} className="btn btn-secondary btn-sm">Anuluj</button>
          <button onClick={save} disabled={busy} className="btn btn-primary btn-sm">
            {busy ? 'Zapisywanie…' : (isEdit ? 'Zapisz zmiany' : 'Dodaj typ')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">{label}</span>
      {children}
    </div>
  );
}
